import React from "react";
import {
  Typography,
  Row,
  Col,
  Modal,
  Button,
} from "antd";
import "../App.css";
import TrainingChart from "./TrainingChart";
import HomeTrainerProgress from "./HomeTrainerProgress";
import SaveTraining from "./SaveTraining";
import HomeTrainerControls from "./HomeTrainerControls";
import FTPControls from "./FTPControls";
import HomeTrainerScreenControl from "./HomeTrainerScreenControl";
import IntervalProgressHud from "./IntervalProgressHud";
import TotalSessionTimeHud from "./TotalSessionTimeHud";
import HomeTrainerConnectControls from "./HomeTrainerConnectControls";
import PlanSetupProgramNavigation from "./PlanSetupProgramNavigation";

const { Text } = Typography;

const HomeTrainerMobile = ({
  // Connection props
  isConnected,
  isBPMConnected,
  simulate,
  pause,
  isMute,
  onConnect,
  onDisconnect,
  connectToBPM,
  disconnectFromBPM,
  onToggleMute,

  // Training data props
  steps,
  initSteps,
  FTP,
  additionalPower,
  indexStep,
  totTime,
  totTimeStep,
  remainingTimeStep,
  totTimeFinal,
  currentTargetPower,
  nextTargetPower,
  powerInfo,

  // Control props
  isAdmin,
  isSessionSaved,
  setIsSessionSaved,
  setSaveModalOpen,
  onPause,
  onReset,
  onSkip,
  onSimulate,
  goNextStep,
  confirm,
  handleAdjustAdditionalFtp,

  // UI state props
  infoToSave,
  // Plan navigation helpers (to match desktop behavior)
  username,
  plansInfo,
  planCategoryForNav,
  previousUuid,
  selectPlan,
  getAllTrainingPlansInfo,
  getPlanLabelByUuid,
  weight,
  setWeight,
  setFTP,
  FTPResult,
  otherUserInfo,
  setOtherUserInfo,
  goToPlanSetup,
  setNavigationWarningOpen,
  setPendingNavigationKey,

  // Utility functions
  formatTime,
  simulateTrainer,

  // Modal states
  planInfoModalIsOpen,
  setPlanInfoModalIsOpen,
  saveModalOpen,

  // Colors
  // Control heights
  controlHeightMobile = 52,
  onRequestPremium,
}) => {
  return (
    <div>
      {/* Plan Info Modal */}
      <Modal
        title={null}
        open={planInfoModalIsOpen}
        onCancel={() => setPlanInfoModalIsOpen(false)}
        footer={
          <div style={{ display: "flex", justifyContent: "center" }}>
            <Button onClick={() => setPlanInfoModalIsOpen(false)} style={{ minWidth: 160 }}>
              Close
            </Button>
          </div>
        }
        width="95%"
      >
        <div>
          <PlanSetupProgramNavigation
            username={username || infoToSave?.selectedPlan?.username || ""}
            selectedPlan={infoToSave?.selectedPlan}
            planName={infoToSave?.selectedPlan?.name || ""}
            planCategory={planCategoryForNav || infoToSave?.selectedPlan?.category || ""}
            previousUuid={previousUuid ?? infoToSave?.selectedPlan?.previous_uuid}
            plansInfo={plansInfo || []}
            selectPlan={selectPlan}
            getAllTrainingPlansInfo={getAllTrainingPlansInfo}
            getPlanLabelByUuid={getPlanLabelByUuid}
            showTrainingForm={false}
            isMobile={true}
          />
          <Text>{infoToSave?.selectedPlan?.description}</Text><p />
          <Text>{steps.length} steps, duration: <strong>{formatTime((steps.map(s => s.duration).reduce(
            (accumulator, currentValue) => accumulator + currentValue,
            0,
          )))}</strong>, Avg Power: <strong>{steps.length === 0 ? 0 : Math.round(steps.map(s => s.duration * s.watt).reduce(
            (accumulator, currentValue) => accumulator + currentValue,
            0,
          ) / (steps.map(s => s.duration).reduce(
            (accumulator, currentValue) => accumulator + currentValue,
            0,
          )))} Watts</strong></Text>
          <p />
          <TrainingChart steps={steps} FTP={FTP} isMobile={true} />
          <p />
          <HomeTrainerProgress
            steps={steps}
            FTP={FTP}
            currentIndex={-1}
            isMobile={true}
          />
        </div>
      </Modal>

      {/* 1) Connection panel */}
      <HomeTrainerConnectControls
        isMobile={true}
        isConnected={isConnected}
        isBPMConnected={isBPMConnected}
        simulate={simulate}
        pause={pause}
        isMute={isMute}
        onConnect={onConnect}
        onDisconnect={onDisconnect}
        connectToBPM={connectToBPM}
        disconnectFromBPM={disconnectFromBPM}
        onToggleMute={onToggleMute}
        controlHeightMobile={controlHeightMobile}
      />

      {/* Controls + total timer + step timer & target (single glass) */}
      <div className="glass-panel glass-section" style={{ marginTop: 8 }}>
        <HomeTrainerControls
          isMobile={true}
          isConnected={isConnected}
          simulate={simulate}
          pause={pause}
          isAdmin={isAdmin}
          isSessionSaved={isSessionSaved}
          totTime={totTime}
          indexStep={indexStep}
          steps={steps}
          totTimeStep={totTimeStep}
          onPause={onPause}
          onReset={onReset}
          onSkip={onSkip}
          onSimulate={onSimulate}
          goNextStep={goNextStep}
          confirm={confirm}
          setIsSessionSaved={setIsSessionSaved}
          setSaveModalOpen={setSaveModalOpen}
          controlHeightMobile={controlHeightMobile}
        />

        {/* Total timer (futuristic HUD) */}
        <div style={{ marginTop: 8 }}>
          <TotalSessionTimeHud
            isMobile={true}
            elapsedSec={totTime}
            totalSec={totTimeFinal}
            formatTime={formatTime}
          />
        </div>
      </div>

      {/* 2) Power gauge */}
      {/* (Removed on mobile) Power gauge + live chart */}

      {/* 4) Two-column area */}
      <Row gutter={8} style={{ marginTop: 8 }}>
        {/* Left column: main screen control */}
        <Col span={14}>
          <div className="glass-panel glass-section">
            <IntervalProgressHud
              isMobile={true}
              remainingTimeSec={remainingTimeStep}
              elapsedTimeSec={totTimeStep}
              formatTime={formatTime}
              style={{ marginBottom: 10 }}
            />
            <HomeTrainerScreenControl
              isMobile={true}
              steps={steps}
              indexStep={indexStep}
              totTimeStep={totTimeStep}
              remainingTimeStep={remainingTimeStep}
              currentTargetPower={currentTargetPower}
              nextTargetPower={nextTargetPower}
              powerInfo={powerInfo}
              pause={pause}
              FTP={FTP}
              formatTime={formatTime}
              tagClassName="custom-tag-phone"
              screenWidthStyle="calc(100% - 20px)"
            />
          </div>
        </Col>

        {/* Right column: training progress */}
        <Col span={10}>
          <div className="glass-panel glass-section" style={{ marginBottom: 8 }}>
            <FTPControls
              isMobile={true}
              FTP={FTP}
              additionalPower={additionalPower}
              onAdjustFTP={handleAdjustAdditionalFtp}
              controlHeightMobile={controlHeightMobile}
            />
          </div>
          <HomeTrainerProgress
            steps={steps}
            FTP={FTP}
            currentIndex={indexStep}
            isMobile={true}
          />
        </Col>
      </Row>

      {/* 5) YouTube embed and video conf */}
      {/* (Removed on mobile) YouTube embed + video conf */}

      {/* Save Training Modal - Mobile */}
      <SaveTraining
        disabled={!pause || (!isConnected && !simulate)}
        weight={weight}
        steps={steps}
        initSteps={initSteps}
        data={powerInfo.data}
        FTP={FTP}
        setFTP={setFTP}
        setWeight={setWeight}
        infoToSave={infoToSave}
        FTPResult={FTPResult}
        otherUserInfo={otherUserInfo}
        setOtherUserInfo={setOtherUserInfo}
        externalOpen={saveModalOpen}
        onSaved={() => {
          setIsSessionSaved(true);
        }}
        onModalClose={() => {
          console.log('SaveTraining onModalClose called');
          setSaveModalOpen(false);
          // If there's pending navigation (like from HomeTrainerProgress), execute it
          // Handle pending navigation if needed
        }}
      />
    </div>
  );
};

export default HomeTrainerMobile;
